import { shareAIChatSession } from '../../../utils/api';

// Open the chat modal pinned to the channel we just posted into, so the user
// sees immediate confirmation that the share landed. Mirrors the helper used
// by the Power BI filter-share flow.
const openChatWithChannel = (channel) => {
  try {
    if (window.openChatModal && typeof window.openChatModal === 'function') {
      window.openChatModal(channel);
    } else {
      const event = new CustomEvent('openChatModal', {
        detail: { channel, action: 'open' }
      });
      window.dispatchEvent(event);
    }
  } catch (error) {
    console.error('Error opening chat modal with channel:', error);
  }
};

// Map Stream Chat sanitized user IDs back to real emails. The AI chat
// sessions container partitions by /userId which stores the user's email, so
// forks must be created with emails — not stream IDs. orgUsers already
// carries both forms from fetchUsersFromOrganization().
const buildStreamIdToEmailMap = (orgUsers, selfUser) => {
  const map = new Map();
  (orgUsers || []).forEach(u => {
    if (u.id && u.email) map.set(u.id, u.email);
  });
  // The current user usually isn't in orgUsers (it filters themselves out),
  // but they may be a member of a channel we're sharing to, so include them.
  if (selfUser?.id && selfUser?.email) map.set(selfUser.id, selfUser.email);
  return map;
};

// Share an existing AI chat session to teammates via Stream Chat.
//
// Mirrors createStreamChatShareModal in shape but:
//  - Different header copy (conversation + message count, not filter counts).
//  - Before sending the chat message, calls /aiChatSessions/share to create
//    a recipient-owned fork so the receiver can continue the conversation
//    independently of the sender's ongoing session.
//  - Uses attachment type 'ai_session_share' (routed by CustomMessageItem to
//    AIChatShareMessage for rendering).
export const createAIChatShareModal = async (sessionInfo, channels, user, chatClient, orgUsers = []) => {
  return new Promise((resolve, reject) => {
    try {
      const streamIdToEmail = buildStreamIdToEmailMap(orgUsers, user);
      const senderEmail = user?.email || streamIdToEmail.get(user?.id) || null;

      const channelListHtml = channels.map(channel => {
        let channelName = 'Unnamed Chat';
        if (channel.data?.name) {
          channelName = channel.data.name;
        } else {
          const otherMembers = Object.values(channel.state.members).filter(m => m.user_id !== user.id);
          if (otherMembers.length > 0) {
            channelName = otherMembers.map(m => m.user?.name || m.user_id).join(', ');
          }
        }
        const memberCount = Object.keys(channel.state.members).length;
        return `
          <button
            class="share-item"
            data-channel-id="${channel.id}"
          >
            <div style="display:flex;align-items:center;justify-content:space-between;width:100%">
              <div style="flex:1">
                <p style="font-weight:500;color:var(--lodge-onyx-800);font-size:14px;margin:0">${channelName}</p>
                <p style="font-size:12px;color:var(--lodge-onyx-400);margin:2px 0 0">${memberCount} member${memberCount !== 1 ? 's' : ''}</p>
              </div>
              <svg style="color:var(--lodge-onyx-300)" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>
          </button>
        `;
      }).join('');

      // Hide users who already have a 1:1 channel with us — they appear in
      // the channel list above, so a second entry in "other team members"
      // would be confusing.
      const existingChatUserIds = new Set();
      channels.forEach(channel => {
        Object.values(channel.state.members).forEach(m => {
          if (m.user_id !== user.id) existingChatUserIds.add(m.user_id);
        });
      });

      const newUsers = orgUsers.filter(u => !existingChatUserIds.has(u.id));

      const newUsersHtml = newUsers.map(u => {
        const initial = (u.name || u.email || 'U').charAt(0).toUpperCase();
        return `
          <button
            class="share-item share-item-user"
            data-user-id="${u.id}"
            data-user-email="${u.email}"
            data-user-name="${u.name}"
          >
            <div style="display:flex;align-items:center;gap:12px;width:100%">
              <div style="width:36px;height:36px;border-radius:8px;background:var(--lodge-orange-400);color:white;display:flex;align-items:center;justify-content:center;font-weight:600;font-size:14px;flex-shrink:0">
                ${initial}
              </div>
              <div style="flex:1;min-width:0">
                <p style="font-weight:500;color:var(--lodge-onyx-800);font-size:14px;margin:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${u.name}</p>
                <p style="font-size:12px;color:var(--lodge-onyx-400);margin:2px 0 0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${u.email}</p>
              </div>
              <svg style="color:var(--lodge-onyx-300)" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>
          </button>
        `;
      }).join('');

      const subtitle = [
        sessionInfo.hotelName || 'AI Conversation',
        sessionInfo.messageCount ? `${sessionInfo.messageCount} message${sessionInfo.messageCount !== 1 ? 's' : ''}` : null
      ].filter(Boolean).join(' • ');

      const modal = document.createElement('div');
      modal.className = 'ai-share-modal-backdrop';
      modal.innerHTML = `
        <div class="ai-share-modal-card">
          <div class="ai-share-modal-header">
            <div style="display:flex;align-items:center;gap:12px">
              <div style="width:40px;height:40px;border-radius:12px;background:var(--lodge-orange-50,#FFF4ED);display:flex;align-items:center;justify-content:center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--lodge-orange-400,#FF7A00)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                </svg>
              </div>
              <div>
                <h3 style="font-size:17px;font-weight:700;color:var(--lodge-onyx-800,#1A1A1F);letter-spacing:-0.2px;margin:0">Share AI Chat</h3>
                <p style="font-size:13px;color:var(--lodge-onyx-400,#72727F);margin:2px 0 0">${subtitle}</p>
              </div>
            </div>
            <button id="close-ai-share-modal" style="width:36px;height:36px;border-radius:8px;display:flex;align-items:center;justify-content:center;color:var(--lodge-onyx-400,#72727F);border:none;background:transparent;cursor:pointer;transition:background 0.15s">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div style="padding:12px 24px 0">
            <div style="position:relative">
              <svg style="position:absolute;left:12px;top:50%;transform:translateY(-50%);color:var(--lodge-onyx-300,#A0A0AE);pointer-events:none" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                id="ai-share-search"
                type="text"
                placeholder="Search conversations or team members..."
                style="width:100%;padding:8px 16px 8px 36px;background:var(--lodge-onyx-50,#F6F6FA);border:1px solid var(--lodge-onyx-100,#E8E8F0);border-radius:12px;font-size:13px;color:var(--lodge-onyx-900,#0F0F11);outline:none;font-family:var(--font-sans,'DM Sans',system-ui,sans-serif);box-sizing:border-box"
              />
            </div>
          </div>

          <div style="padding:8px 24px 4px;font-size:12px;color:var(--lodge-onyx-400,#72727F);line-height:1.4">
            Recipients get a point-in-time copy of this conversation. Follow-ups you send here won't be visible to them.
          </div>

          <div style="padding:8px 24px 20px;overflow-y:auto;max-height:380px" id="ai-share-list">
            ${channels.length > 0 ? `
              <p style="font-size:11px;font-weight:600;color:var(--lodge-onyx-400,#72727F);text-transform:uppercase;letter-spacing:0.5px;margin:0 0 8px">
                Existing Conversations
              </p>
              <div id="ai-channel-list" style="margin-bottom:16px">
                ${channelListHtml}
              </div>
            ` : ''}

            ${newUsers.length > 0 ? `
              <p style="font-size:11px;font-weight:600;color:var(--lodge-onyx-400,#72727F);text-transform:uppercase;letter-spacing:0.5px;margin:0 0 8px">
                Other Team Members
              </p>
              <div id="ai-user-list">
                ${newUsersHtml}
              </div>
            ` : ''}

            ${channels.length === 0 && newUsers.length === 0 ? `
              <div style="text-align:center;padding:32px 0;color:var(--lodge-onyx-400,#72727F)">
                <p style="font-size:14px;margin:0">No conversations or team members available</p>
                <p style="font-size:12px;margin:4px 0 0">Start a conversation to share AI chats</p>
              </div>
            ` : ''}
          </div>
        </div>
      `;

      // Same CSS class names as the filter-share modal's stylesheet would use
      // a name collision if both modals were open; prefix keeps them distinct.
      const styleElement = document.createElement('style');
      styleElement.textContent = `
        .ai-share-modal-backdrop {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(15,15,17,.4);
          backdrop-filter: blur(4px);
          z-index: 100;
        }

        .ai-share-modal-card {
          background: var(--lodge-white, #fff);
          border-radius: var(--radius-xl, 16px);
          box-shadow: var(--shadow-xl, 0 24px 48px -12px rgba(15,15,17,.18));
          max-width: 480px;
          width: calc(100% - 32px);
          max-height: 80vh;
          overflow: hidden;
          animation: aiShareIn 0.3s cubic-bezier(0,0,.2,1) forwards;
          font-family: var(--font-sans, 'DM Sans', system-ui, sans-serif);
          display: flex;
          flex-direction: column;
        }

        @keyframes aiShareIn {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .ai-share-modal-header {
          padding: 24px;
          border-bottom: 1px solid var(--lodge-onyx-100, #E8E8F0);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .ai-share-modal-backdrop .share-item {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 12px;
          border-radius: 12px;
          border: 1px solid transparent;
          background: transparent;
          cursor: pointer;
          font-family: inherit;
          transition: all 0.15s;
          margin-bottom: 4px;
          text-align: left;
        }

        .ai-share-modal-backdrop .share-item:hover {
          background: var(--lodge-onyx-50, #F6F6FA);
          border-color: var(--lodge-onyx-100, #E8E8F0);
        }

        #close-ai-share-modal:hover {
          background: var(--lodge-onyx-50, #F6F6FA);
        }

        #ai-share-search:focus {
          border-color: var(--lodge-orange-400, #FF7A00);
          box-shadow: 0 0 0 3px rgba(255,122,0,.12);
        }
      `;
      document.head.appendChild(styleElement);
      document.body.appendChild(modal);

      const searchInput = modal.querySelector('#ai-share-search');
      searchInput.addEventListener('input', (e) => {
        const query = e.target.value.toLowerCase();
        const items = modal.querySelectorAll('.share-item');
        items.forEach(item => {
          const text = item.textContent.toLowerCase();
          item.style.display = text.includes(query) ? '' : 'none';
        });

        const channelSection = modal.querySelector('#ai-channel-list');
        const userSection = modal.querySelector('#ai-user-list');
        if (channelSection) {
          const visibleChannels = channelSection.querySelectorAll('.share-item:not([style*="display: none"])');
          const header = channelSection.previousElementSibling;
          if (header) header.style.display = visibleChannels.length > 0 ? '' : 'none';
        }
        if (userSection) {
          const visibleUsers = userSection.querySelectorAll('.share-item:not([style*="display: none"])');
          const header = userSection.previousElementSibling;
          if (header) header.style.display = visibleUsers.length > 0 ? '' : 'none';
        }
      });

      const closeModal = () => {
        if (document.body.contains(modal)) document.body.removeChild(modal);
        if (document.head.contains(styleElement)) document.head.removeChild(styleElement);
      };

      modal.querySelector('#close-ai-share-modal').addEventListener('click', () => { closeModal(); resolve(null); });
      modal.addEventListener('click', (e) => { if (e.target === modal) { closeModal(); resolve(null); } });

      const showSuccess = (recipientName) => {
        const successEl = document.createElement('div');
        successEl.style.cssText = `
          position:fixed;top:24px;right:24px;
          background:var(--lodge-white,#fff);border-left:4px solid var(--color-success,#12B76A);
          box-shadow:var(--shadow-lg,0 12px 24px -4px rgba(15,15,17,.12));
          border-radius:0 12px 12px 0;padding:16px;
          display:flex;align-items:flex-start;gap:12px;
          z-index:200;
          font-family:var(--font-sans,'DM Sans',system-ui,sans-serif);
          animation:aiShareIn 0.3s cubic-bezier(0,0,.2,1) forwards;
        `;
        successEl.innerHTML = `
          <svg style="color:var(--color-success,#12B76A);flex-shrink:0;margin-top:1px" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <div>
            <p style="font-weight:600;color:var(--lodge-onyx-800,#1A1A1F);font-size:14px;margin:0">Shared successfully</p>
            <p style="font-size:13px;color:var(--lodge-onyx-400,#72727F);margin:2px 0 0">Shared with ${recipientName}</p>
          </div>
        `;
        document.body.appendChild(successEl);

        setTimeout(() => {
          successEl.style.opacity = '0';
          successEl.style.transition = 'opacity 0.3s';
          setTimeout(() => { if (document.body.contains(successEl)) document.body.removeChild(successEl); }, 300);
        }, 2000);
      };

      // Resolve recipient emails for a channel by mapping its non-self
      // members through the stream-id → email table. Members without a known
      // email (e.g. users outside the org list, which shouldn't normally
      // happen) are dropped — we can't fork the session to an unknown user.
      const channelRecipientEmails = (channel) => {
        const members = Object.values(channel.state.members || {});
        const out = [];
        const senderKey = (senderEmail || '').toLowerCase();
        for (const m of members) {
          if (m.user_id === user.id) continue;
          const email = streamIdToEmail.get(m.user_id);
          if (email && email.toLowerCase() !== senderKey) out.push(email.toLowerCase());
        }
        return out;
      };

      // Fork the session on the backend, then post a Stream Chat message
      // referencing the recipient's fork threadId so the "Open in AI
      // Assistant" CTA navigates them to their own copy.
      const forkAndSend = async (sendTarget, recipientEmails, targetDisplayName) => {
        if (!recipientEmails.length) {
          throw new Error('No valid recipients for this conversation');
        }

        const shareRes = await shareAIChatSession(sessionInfo.sessionId, recipientEmails);
        if (!shareRes || shareRes.success === false) {
          throw new Error(shareRes?.error || 'Share failed');
        }

        // Pair each recipient email with the fork threadId the backend created
        // for them. The message attachment embeds the list so each viewer's
        // client can navigate to the thread owned by them.
        const forksByEmail = {};
        (shareRes.forks || []).forEach(f => { forksByEmail[f.recipientUserId] = f.forkThreadId; });

        const senderDisplayName = shareRes.senderName || user?.name || user?.email || 'A teammate';

        const attachment = {
          type: 'ai_session_share',
          sessionInfo: {
            hotelName: sessionInfo.hotelName || 'AI Conversation',
            messageCount: sessionInfo.messageCount || 0,
            lastMessage: sessionInfo.lastMessage || '',
            originalThreadId: sessionInfo.threadId || null
          },
          forksByEmail,
          senderName: senderDisplayName,
          sharedAt: new Date().toISOString()
        };

        await sendTarget.sendMessage({
          text: `${senderDisplayName} shared an AI chat: ${sessionInfo.hotelName || 'AI Conversation'}`,
          attachments: [attachment]
        });

        showSuccess(targetDisplayName || 'recipient');
        closeModal();
        setTimeout(() => openChatWithChannel(sendTarget), 500);
        resolve(sendTarget);
      };

      const shareList = modal.querySelector('#ai-share-list');
      shareList.addEventListener('click', async (e) => {
        const channelButton = e.target.closest('[data-channel-id]');
        const userButton = e.target.closest('[data-user-id]');

        if (channelButton) {
          const channelId = channelButton.dataset.channelId;
          const selectedChannel = channels.find(c => c.id === channelId);
          if (!selectedChannel) return;

          channelButton.style.opacity = '0.5';
          channelButton.style.pointerEvents = 'none';

          try {
            const recipientEmails = channelRecipientEmails(selectedChannel);
            const displayName = selectedChannel.data?.name ||
              Object.values(selectedChannel.state.members)
                .filter(m => m.user_id !== user.id)
                .map(m => m.user?.name || m.user_id)
                .join(', ') || 'recipient';
            await forkAndSend(selectedChannel, recipientEmails, displayName);
          } catch (error) {
            console.error('Error sharing AI chat to channel:', error);
            channelButton.style.opacity = '1';
            channelButton.style.pointerEvents = '';
            alert(`Couldn't share this chat: ${error.message || 'Unknown error'}`);
            reject(error);
          }
        } else if (userButton) {
          const recipientEmail = userButton.dataset.userEmail;
          const streamUserId = userButton.dataset.userId;
          const userName = userButton.dataset.userName;

          userButton.style.opacity = '0.5';
          userButton.style.pointerEvents = 'none';

          try {
            if (!chatClient) throw new Error('Chat client not available');
            if (!recipientEmail) throw new Error('Missing recipient email');

            const channel = chatClient.channel('messaging', {
              members: [user.id, streamUserId],
              name: `Chat with ${userName}`,
            });
            await channel.create();
            await channel.watch();

            await forkAndSend(channel, [recipientEmail.toLowerCase()], userName);
          } catch (error) {
            console.error('Error sharing AI chat to new user:', error);
            userButton.style.opacity = '1';
            userButton.style.pointerEvents = '';
            alert(`Couldn't share this chat: ${error.message || 'Unknown error'}`);
            reject(error);
          }
        }
      });

    } catch (error) {
      console.error('Error creating AI chat share modal:', error);
      reject(error);
    }
  });
};
